/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'

import { EuiCallOut } from '@elastic/eui'

import type { Tag } from '@modules/resources-shared-components/Tags/types'
import TagsFlyout from '@modules/resources-shared-components/Tags/TagsFlyout'
import { useUpdateDeploymentMutation } from '@modules/cloud-lib/deployments/hooks'
import { FormattedMessage } from '@modules/i18n'
import { addToast } from '@modules/cui/Toasts'
import { withErrorBoundary } from '@modules/cui/Boundary'
import type { BasicFailedReplyElement } from '@modules/cloud-api/v1/types'

import { validateTag } from './validateTag'

import type { ReactElement } from 'react'

export type Props = {
  tags: Tag[]
  onClose: () => void
  deploymentId: string
}

const getErrorTitle = (errors: BasicFailedReplyElement[]) => {
  if (errors.length === 1) {
    return errors[0]?.message
  }

  return (
    <FormattedMessage id='cui-alert.something-went-wrong' defaultMessage='Something went wrong' />
  )
}

const DeploymentTagsFlyout = ({ tags: initialTags, onClose, deploymentId }: Props) => {
  const {
    mutate: updateDeploymentMutate,
    isLoading,
    error,
  } = useUpdateDeploymentMutation({
    onSuccess: onClose,
    onError: ({ errors }) => {
      addToast({
        family: 'resource-tags.flyout',
        color: 'danger',
        title: (
          <FormattedMessage
            id='resource-tags.flyout.failure'
            defaultMessage='Could not save tags'
          />
        ),
        text: getErrorTitle(errors),
      })
    },
  })

  const onSaveTagsMutation = async (tags: Tag[]) => {
    await updateDeploymentMutate({
      params: {
        deploymentId,
      },
      body: {
        prune_orphans: false,
        metadata: {
          tags,
        },
      },
    })
  }

  const errorCallout: ReactElement<typeof EuiCallOut> | null = error && (
    <EuiCallOut announceOnMount={true} color='danger' title={getErrorTitle(error.errors)}>
      {error.errors.length > 1 && (
        <ul>
          {error.errors.map((e, index) => (
            <li key={index} data-error-code={e.code}>
              {e.message}
            </li>
          ))}
        </ul>
      )}
    </EuiCallOut>
  )

  return (
    <TagsFlyout
      initialTags={initialTags}
      onClose={onClose}
      onSaveTagsMutation={onSaveTagsMutation}
      isLoading={isLoading}
      resourceType={
        <FormattedMessage id='deployment-tags.resource-type' defaultMessage='deployment' />
      }
      resourceTypePlural={
        <FormattedMessage id='deployment-tags.resource-type-plural' defaultMessage='deployments' />
      }
      validateTag={validateTag}
      errorCallout={errorCallout}
    />
  )
}

export default withErrorBoundary(DeploymentTagsFlyout)
